import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { PendingImport } from 'modules/manager/ui/hooks/use-import-templates';
import React, { useCallback, useState } from 'react';
import UDCheckbox from 'ud-form/components/checkbox/component';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

interface ImportConflictsModalProps {
  pending: PendingImport;
  /** Receives the `entryIndex` of every template to overwrite; an empty list is valid. */
  onConfirm: (entryIndexes: number[]) => void;
  onCancel: () => void;
}

/**
 * One decision for every same-named template in the file. Nothing is checked by
 * default: overwriting cannot be undone, and for presets it reaches every
 * organization at once — so it is a deliberate choice, never the default.
 */
function ImportConflictsModal({ pending, onConfirm, onCancel }: ImportConflictsModalProps) {
  const { t } = useLingui();
  const { conflicts, isPreset } = pending;
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const allSelected = selected.size === conflicts.length;
  const conflictsCount = conflicts.length;
  const selectedCount = selected.size;

  const toggle = useCallback((entryIndex: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(entryIndex)) next.delete(entryIndex);
      else next.add(entryIndex);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(conflicts.map((conflict) => conflict.entryIndex)));
  }, [allSelected, conflicts]);

  const subtitle =
    conflictsCount === 1
      ? t`Шаблон из файла называется так же, как уже существующий. Перезаписать его содержимым из файла?`
      : plural(conflictsCount, {
          one: 'В файле # шаблон с именем, которое уже занято. Отметьте, какие перезаписать содержимым из файла.',
          few: 'В файле # шаблона с именами, которые уже заняты. Отметьте, какие перезаписать содержимым из файла.',
          many: 'В файле # шаблонов с именами, которые уже заняты. Отметьте, какие перезаписать содержимым из файла.',
          other: 'В файле # шаблона с именами, которые уже заняты. Отметьте, какие перезаписать содержимым из файла.',
        });

  return (
    <Modal show onClose={onCancel} size="lg">
      <Modal.Header title={isPreset ? t`Такие пресеты уже есть` : t`Такие шаблоны уже есть`} subtitle={subtitle} />
      <Modal.Body className="flex flex-col gap-3">
        {isPreset && (
          <UDText variant="body-3" className="bg-CasePendingSurface text-CasePendingText rounded-card px-3 py-2">
            <Trans>Перезаписанный пресет сразу изменится у всех организаций. Отменить перезапись нельзя.</Trans>
          </UDText>
        )}

        {/* A div, not a label: `UDCheckbox` is a label itself, and nested labels toggle twice. */}
        <div className="border-lineSubtle flex items-center gap-3 border-b pb-2">
          <UDCheckbox checked={allSelected} onChange={toggleAll} aria-label={t`Выбрать все`} />
          <span className="cursor-pointer" onClick={toggleAll}>
            <UDText variant="body-2" className="text-inkPrimary font-semibold">
              <Trans>Выбрать все</Trans>
            </UDText>
          </span>
        </div>

        {/* No scroll of its own: `UDModal` already scrolls past 90vh, and a nested
            `overflow-y: auto` showed a scrollbar for a single row — its content pokes a
            pixel out of the box. */}
        <ul className="flex flex-col gap-2" aria-label={t`Совпавшие шаблоны`}>
          {conflicts.map((conflict) => {
            const isChecked = selected.has(conflict.entryIndex);
            const { existingName, fallbackName } = conflict;
            return (
              <li key={conflict.entryIndex}>
                <div className="flex items-center gap-3">
                  <UDCheckbox
                    checked={isChecked}
                    onChange={() => toggle(conflict.entryIndex)}
                    aria-label={t`Перезаписать «${existingName}»`}
                  />
                  <span
                    className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-4"
                    onClick={() => toggle(conflict.entryIndex)}
                  >
                    <UDText variant="body-2" className="text-inkPrimary truncate">
                      {existingName}
                    </UDText>
                    <UDText variant="body-3" className="text-inkSecondary shrink-0">
                      {isChecked ? t`будет перезаписан` : fallbackName ? t`создастся как «${fallbackName}»` : t`будет пропущен`}
                    </UDText>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="secondary" onClick={onCancel}>
          <Trans>Отмена</Trans>
        </UDButton>
        <UDButton variant="primary" onClick={() => onConfirm(Array.from(selected))}>
          {selectedCount > 0 ? t`Импортировать и перезаписать ${selectedCount}` : t`Импортировать`}
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
}

export default ImportConflictsModal;
